import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { usePermissions } from '@hooks/usePermissions';
import { getMonthName } from '@utils/dates/dateMethods';
import { getCurrency } from '@utils/tools/currencyTools';
import { ConditionalRendering } from '../../../utils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import ColumnApplicationType from './ColumnApplicationType';
import ColumnElectedAPTC from './ColumnElectedAPTC';
import ColumnGrossPremium from './ColumnGrossPremium';
import ColumnMaxAptc from './ColumnMaxAptc';
import ColumnMaxStateSubsidy from './ColumnMaxStateSubsidy';
import ColumnNerPremium from './ColumnNetPremium';
import ColumnStateSubsidy2Amount from './ColumnStateSubsidy2Amount';
import ColumnStateSubsidyAmount from './ColumnStateSubsidyAmount';

const PremiumHistoryColumnDef = ({ enrollment }) => {
    const intl = useIntl();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();
    const { isPremiumEditPermission, isMemberEnrlmntAptcEditable, isNotApplicable, ehbPremiumValue, showPremiumValue } =
        usePermissions();

    const getColumnDefination = useMemo(() => {
        const columnHeaders = [
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.month' }),
                key: 'month',
                render: (premium: { month: number }) => <span>{getMonthName(premium.month)}</span>,
            },
        ];

        const addColumn = (condition: boolean, column: any) => {
            if (condition) columnHeaders.push(column);
        };

        const columns = [
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.grossPremium' }),
                    key: 'grossPremium',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnGrossPremium premium={premium} />,
                },
            },
            {
                condition: isPremiumEditPermission(userPermissions),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.groupMaxAPTC' }),
                    key: 'maxAptc',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnMaxAptc premium={premium} />,
                },
            },
            {
                condition: isMemberEnrlmntAptcEditable(userPermissions),
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.electedAPTC' }),
                    key: 'electedAPTC',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnElectedAPTC premium={premium} />,
                },
            },
            {
                condition:
                    enrollment.isStateSubsidyEnabled &&
                    (userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable),
                column: {
                    titleId: intl.formatMessage({
                        id: 'member.enrollment_list.premiumhistory.groupMaxStateSubsidy',
                    }),
                    key: 'maxStateSubsidy',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnMaxStateSubsidy premium={premium} />,
                },
            },
            {
                condition:
                    enrollment.isStateSubsidyEnabled &&
                    (userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable),
                column: {
                    titleId: intl.formatMessage({
                        id: 'member.enrollment_list.premiumhistory.electedStateSubsidy',
                    }),
                    key: 'stateSubsidyAmount',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnStateSubsidyAmount premium={premium} />,
                },
            },
            {
                condition:
                    enrollment.isStateSubsidy2Enabled &&
                    (userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable),
                column: {
                    titleId: intl.formatMessage({
                        id: 'member.enrollment_list.premiumhistory.groupMaxStateSubsidy2',
                    }),
                    key: 'maxStateSubsidy2',
                    render: (premium: IEnrollmentPremiumDto) =>
                        premium?.maxStateSubsidy2 !== null ? getCurrency(premium?.maxStateSubsidy2) : '',
                },
            },
            {
                condition:
                    enrollment.isStateSubsidy2Enabled &&
                    (userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable),
                column: {
                    titleId: intl.formatMessage({
                        id: 'member.enrollment_list.premiumhistory.electedStateSubsidy2',
                    }),
                    key: 'stateSubsidy2Amount',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnStateSubsidy2Amount premium={premium} />,
                },
            },
            {
                condition: enrollment.statePremiumCreditAmt,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.premiumCredit' }),
                    key: 'premiumCredit',
                    render: (premium: { month: number; statePremiumCreditAmt: number }) => (
                        <>{showPremiumValue(premium, enrollment) ? getCurrency(premium.statePremiumCreditAmt) : ''}</>
                    ),
                },
            },
            {
                condition: enrollment.isMedicaidTransitionEnabled,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.mtpr' }),
                    key: 'medicaidTransitionAmount',
                    render: (premium: { month: number; medicaidTransitionAmount: number }) => (
                        <>{getCurrency(premium?.medicaidTransitionAmount) || ''}</>
                    ),
                },
            },
            {
                condition: true,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.netPremium' }),
                    key: 'netPremium',
                    render: (premium: IEnrollmentPremiumDto) => <ColumnNerPremium premium={premium} />,
                },
            },
            {
                condition: userPermissions?.memberEnrlmntAppType,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.applicationType' }),
                    key: 'applicationType',
                    render: (premium: IEnrollmentPremiumDto) => (
                        <ColumnApplicationType
                            premium={premium}
                            isMedicaidTransitionEnabled={enrollment.isMedicaidTransitionEnabled}
                        />
                    ),
                },
            },
            {
                condition: userPermissions?.memberEnrlmntViewSlcsp || userPermissions?.memberEnrlmntEditSlcsp,
                column: {
                    titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.slcsp' }),
                    key: 'slcspPremiumAmount',
                    render: (premium: IEnrollmentPremiumDto) => (
                        <span data-testid={`slcsp-${premium.month}`}>
                            {getCurrency(premium?.slcspPremiumAmount) || ''}
                        </span>
                    ),
                },
            },
            {
                condition: userPermissions?.memberEnrlmntEhbprmum || userPermissions?.memberEnrlmntEhbPrmumEditable,
                column: {
                    titleId: `${intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.ehbPremium' })} (${
                        enrollment.ehbPercentage != null && enrollment.ehbPercentage !== 0
                            ? enrollment.ehbPercentage > 1
                                ? enrollment.ehbPercentage
                                : +(enrollment.ehbPercentage * 100).toFixed(2)
                            : 0
                    } %)`,
                    key: 'ehbPremium',
                    render: (premium: IEnrollmentPremiumDto) => (
                        <ConditionalRendering isTrue={isNotApplicable(premium, values)}>
                            {ehbPremiumValue(premium, enrollment)}
                        </ConditionalRendering>
                    ),
                },
            },
        ];

        columns.forEach(({ condition, column }) => addColumn(condition, column));

        return columnHeaders;
    }, [enrollment, userPermissions, intl, values, errors]);

    return getColumnDefination;
};

export default PremiumHistoryColumnDef;
